import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { Hash, Plus, Trash2, Edit2, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

export default function Hashtags() {
    const { api } = useAuth();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        hashtags: ''
    });

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/hashtag-groups');
            setGroups(response.data);
        } catch (error) {
            console.error('Failed to fetch groups:', error);
            toast.error('Failed to load hashtag groups');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const openCreateDialog = () => {
        setEditingGroup(null);
        setFormData({ name: '', hashtags: '' });
        setDialogOpen(true);
    };

    const openEditDialog = (group) => {
        setEditingGroup(group);
        setFormData({
            name: group.name,
            hashtags: group.hashtags.join(' ')
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter a group name');
            return;
        }

        const hashtags = formData.hashtags
            .split(/[\s,]+/)
            .map(tag => tag.startsWith('#') ? tag : `#${tag}`)
            .filter(tag => tag.length > 1);

        if (hashtags.length === 0) {
            toast.error('Please enter at least one hashtag');
            return;
        }

        setSaving(true);
        try {
            const data = { name: formData.name, hashtags };
            if (editingGroup) {
                await api.put(`/hashtag-groups/${editingGroup.id}`, data);
                toast.success('Hashtag group updated');
            } else {
                await api.post('/hashtag-groups', data);
                toast.success('Hashtag group created');
            }
            setDialogOpen(false);
            fetchGroups();
        } catch (error) {
            console.error('Failed to save group:', error);
            toast.error('Failed to save hashtag group');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!groupToDelete) return;
        try {
            await api.delete(`/hashtag-groups/${groupToDelete.id}`);
            toast.success('Hashtag group deleted');
            setGroups(prev => prev.filter(g => g.id !== groupToDelete.id));
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
        } catch (error) {
            console.error('Failed to delete group:', error);
            toast.error('Failed to delete hashtag group');
        }
    };

    const copyToClipboard = (hashtags) => {
        navigator.clipboard.writeText(hashtags.join(' '));
        toast.success('Hashtags copied to clipboard');
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="hashtags-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Hashtag Groups</h1>
                    <p className="text-muted-foreground mt-1">
                        Organize and reuse your hashtag collections
                    </p>
                </div>
                <Button
                    className="gradient-bg"
                    onClick={openCreateDialog}
                    data-testid="create-group-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Group
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : groups.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                        <Hash className="h-16 w-16 mb-4 opacity-50" />
                        <p className="text-lg font-medium">No hashtag groups yet</p>
                        <p className="text-sm">Create groups to organize and reuse hashtags</p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={openCreateDialog}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Create Your First Group
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groups.map(group => (
                        <Card
                            key={group.id}
                            className="hover-lift cursor-pointer group"
                            onClick={() => copyToClipboard(group.hashtags)}
                            data-testid={`hashtag-group-${group.id}`}
                        >
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Hash className="h-5 w-5 text-primary" />
                                        {group.name}
                                    </CardTitle>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditDialog(group);
                                            }}
                                        >
                                            <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-destructive hover:text-destructive"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setGroupToDelete(group);
                                                setDeleteDialogOpen(true);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-1.5">
                                    {group.hashtags.slice(0, 10).map(tag => (
                                        <Badge key={tag} variant="secondary" className="text-xs">
                                            {tag}
                                        </Badge>
                                    ))}
                                    {group.hashtags.length > 10 && (
                                        <Badge variant="outline" className="text-xs">
                                            +{group.hashtags.length - 10} more
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Click to copy • {group.hashtags.length} hashtags
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingGroup ? 'Edit Hashtag Group' : 'Create Hashtag Group'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Group Name</Label>
                            <Input
                                id="name"
                                placeholder="e.g., Travel Photography"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                data-testid="group-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="hashtags">Hashtags</Label>
                            <Textarea
                                id="hashtags"
                                placeholder="#travel #photography #wanderlust..."
                                value={formData.hashtags}
                                onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                                className="min-h-[150px]"
                                data-testid="hashtags-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Separate hashtags with spaces or commas. The # symbol is optional.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            {editingGroup ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Hashtag Group</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete "{groupToDelete?.name}"?</p>
                    <p className="text-sm text-muted-foreground">
                        This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
